import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  Button,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneInput from 'react-phone-input-2';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { string, object, number } from 'yup';
import { Global, css } from '@emotion/react';
import useOrganization from '../../hooks/UseOrganization';
import { commonSlice } from '../../state/reducers/common';
import REGEX from '../../const/Regex';
import DialogTransition from '../Common/DialogTransition';
import { ReactComponent as CloseIcon } from '../../assets/CloseIcon.svg';
import allowedCharacters from '../Common/SpecialCharacters/SpecialCharacter';
import 'react-phone-input-2/lib/style.css';

const PhoneInputCustom = (props) => {
  const { handleActionChange, themeing, ...otherProps } = props;

  return (
    <>
      <Global
        styles={css`
          .react-tel-input .country-list .country.active {
            background-color: ${themeing.palette.error.main} !important;
            color: ${themeing.palette.common.white} !important;
          }
          .react-tel-input .country-list .country:hover,
          .react-tel-input .country-list .country.highlight {
            background-color: ${themeing?.palette?.other
              ?.listTableRowSelectedBg} !important;
          }
        `}
      />

      <PhoneInput
        country="in"
        preferredCountries={['in']}
        disableSearchIcon
        autoFormat={true}
        {...otherProps}
        containerStyle={{ width: '100%', border: 'none' }}
        inputStyle={{
          width: '100%',
          border: 'none',
          outline: 'none',
          paddingBottom: '10px',
          background: 'none',
          color: themeing.palette.warning.contrastText,
        }}
        buttonStyle={{
          border: 'none',
          backgroundColor: 'transparent',
          height: '38px',
          marginTop: '9px',
          '&.react-tel-input .selected-flag .arrow': {
            display: 'none',
          },
        }}
        dropdownStyle={{
          backgroundColor: themeing.palette?.background?.paper,
          color: themeing.palette.warning.contrastText,
          zIndex: 9999,
        }}
      />
    </>
  );
};

/**
 * @function CreateEditOrgModal
 * @description Renders a form for managing CreateEditOrgModal settings of an organization, including various details. It supports creating a new organization or editing an existing one.
 * @param {object} state - The state object containing the mode and other relevant information.
 * @param {string} [state.mode] - The mode of the form, either 'edit' or 'create'.
 * @returns {JSX} returns the UI of CreateEditOrgModal.
 * @example <CreateEditOrgModal state={state} />
 */

const CreateEditOrgModal = ({
  stateOfForm,
  open,
  setOpen,
  organizationDetailsList,
  pageIndexData,
  pageSizeData,
  searchTermData,
  selectedOrg,
}) => {
  const { createOrganization, organizationDetail, updateOrganization } =
    useOrganization();
  const [isActive, setIsActive] = useState(true);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [countryCode, setCountryCode] = useState('');

  const validatePhoneNumber = useCallback(
    (value) => {
      if (countryCode.length === 0) {
        return true;
      }
      const newValue = value.slice(countryCode?.length || 0);
      return /^\d{10}$/.test(newValue);
    },
    [countryCode]
  );

  const formikForm = useFormik({
    initialValues: {
      organization_fullname: '',
      display_name: '',
      user: '',
      contact_email: '',
      contact_number: '',
    },
    validationSchema: object({
      organization_fullname: string()
        .matches(REGEX.ORGANIZATION_ID, 'Invalid organization ID')
        .min(2, 'Organization ID must be at least 2 characters')
        .max(63, 'Organization ID must be at most 63 characters')
        .required('Required'),
      display_name: string()
        .matches(REGEX.ORGANIZATION_NAME, 'Invalid display name')
        .min(2, 'Display name must be at least 2 characters')
        .max(150, 'Display name must be at most 150 characters')
        .required('Required'),
      user: number()
        .integer('Number of users must be an integer')
        .min(1, 'Users must be greater than or equal to 1')
        .max(50000, 'User must be less than or equal to 50000')
        .required('Required'),
      contact_email: string()
        .email('Email address is not valid')
        .matches(REGEX.EMAIL, 'Email address is not valid')
        .max(254, 'Email must be at most 254 characters')
        .required('Required'),
      contact_number: string()
        .required('Required')
        .test(
          'is-valid-length',
          'Phone number should be 10 digits long (excluding country code)',
          (value) => validatePhoneNumber(value)
        ),
    }),
    onSubmit: (values) => {
      let contactNumber = values.contact_number;
      if (!contactNumber.startsWith('+')) {
        contactNumber = `+${contactNumber}`;
      }

      const payload = {
        name: values.organization_fullname,
        is_active: isActive,
        display_name: values.display_name,
        user_required: values.user,
        contact_email: values.contact_email,
        contact_number: contactNumber,
        schema_name: values.organization_fullname,
      };
      if (stateOfForm === 'edit' && selectedOrg) {
        const patchPayload = {
          ...(formData?.name !== values.organization_fullname
            ? { name: values.organization_fullname }
            : {}),
          ...(formData?.contact_email !== values.contact_email
            ? { contact_email: values.contact_email }
            : {}),
          ...(formData?.contact_number !== values.contact_number
            ? { contact_number: contactNumber }
            : {}),
          ...(formData?.is_active !== isActive ? { is_active: isActive } : {}),
          ...(formData?.display_name !== values.display_name
            ? { display_name: values.display_name }
            : {}),
          ...(formData?.user_required !== values.user
            ? { user_required: values.user }
            : {}),
        };

        updateOrganization(patchPayload, selectedOrg).then(() => {
          formikForm.handleReset();
          setOpen((prev) => !prev);
          organizationDetailsList(pageIndexData, pageSizeData, searchTermData);
        });
      } else {
        createOrganization(payload).then(() => {
          formikForm.handleReset();
          setOpen((prev) => !prev);
          organizationDetailsList().then((res) => {
            dispatch(commonSlice.actions.setOrganizationList(res?.data?.data));
          });
          dispatch(
            commonSlice.actions.setShouldRefreshOrgList((prev) => !prev)
          );
        });
      }
    },
  });

  const handleReset = useCallback(() => {
    formikForm.handleReset();
    setCountryCode('91');
    formikForm.setFieldValue('contact_number', '', true);
    if (stateOfForm === 'edit') {
      formikForm.setFieldValue('organization_fullname', formData?.name || '');
      formikForm.setFieldValue('contact_number', '91');
    }
  }, [formikForm, setCountryCode, stateOfForm, formData]);

  useEffect(() => {
    if (stateOfForm === 'edit' && selectedOrg) {
      organizationDetail(selectedOrg).then((resp) => {
        formikForm.setValues({
          organization_fullname: resp?.data?.name,
          display_name: resp?.data?.display_name,
          user: resp?.data?.user_required,
          contact_email: resp?.data?.contact_email,
          contact_number: resp?.data?.contact_number,
        });
        setFormData(resp?.data);
        setIsActive(resp?.data?.is_active);
      });
    }
  }, [stateOfForm, selectedOrg]);

  const handleBlur = useCallback(
    (e) => {
      formikForm.handleBlur(e); // Default Formik onBlur handling
      formikForm.setFieldTouched('contact_number', true); // Manually set the field as touched
    },
    [formikForm]
  );

  const handleOnClose = useCallback(() => {
    setOpen((prev) => !prev);
    formikForm.handleReset();
  }, [formikForm, setOpen]);

  return (
    <Dialog
      maxWidth="xs"
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>
        {stateOfForm === 'edit' ? 'Edit Organization' : 'Create Organization'}
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        sx={{ position: 'absolute', right: 16, top: 8 }}
        onClick={handleOnClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container direction="row" spacing={2}>
          {/* Organization ID */}
          <Grid item xs={12}>
            <TextField
              disabled={stateOfForm === 'edit'}
              required
              label="Organization ID"
              fullWidth
              name="organization_fullname"
              placeholder="Enter organization ID"
              value={formikForm.values.organization_fullname}
              onChange={formikForm.handleChange}
              onBlur={formikForm.handleBlur}
              error={
                formikForm.touched.organization_fullname &&
                Boolean(formikForm.errors.organization_fullname)
              }
              helperText={
                formikForm.touched.organization_fullname &&
                formikForm.errors.organization_fullname
              }
              InputProps={{
                endAdornment: (
                  <Tooltip
                    title={
                      <Typography>
                        - Organization name must start with a letter. <br />
                        - Numbers (0–9) are allowed. <br />
                        - Underscores (_) and hyphens (-) are allowed. <br />
                        - Spaces are not allowed. <br />- It must not start with{' '}
                        <code>pg_</code> or <code>PG_</code>.
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          {/* Display Name */}
          <Grid item xs={12}>
            <TextField
              required
              label="Display name"
              fullWidth
              name="display_name"
              placeholder="Enter display name"
              value={formikForm.values.display_name}
              onChange={formikForm.handleChange}
              onBlur={formikForm.handleBlur}
              error={
                formikForm.touched.display_name &&
                Boolean(formikForm.errors.display_name)
              }
              helperText={
                formikForm.touched.display_name &&
                formikForm.errors.display_name
              }
              InputProps={{
                endAdornment: (
                  <Tooltip
                    title={
                      <Typography>
                        - Display name should start with a character. <br />
                        - Numbers (0-9) are allowed. <br />
                        - Underscore (_) and hyphen (-) are allowed. <br />-
                        Only single space allowed.
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          {/* User Required */}
          <Grid item xs={12}>
            <TextField
              required
              label="User required"
              fullWidth
              placeholder="Enter number of users required"
              name="user"
              type="number"
              onKeyDown={(e) =>
                allowedCharacters.includes(e.key) && e.preventDefault()
              }
              InputLabelProps={{
                shrink: true,
              }}
              value={formikForm.values.user}
              onChange={formikForm.handleChange}
              onBlur={formikForm.handleBlur}
              error={formikForm.touched.user && Boolean(formikForm.errors.user)}
              helperText={formikForm.touched.user && formikForm.errors.user}
            />
          </Grid>
          {/* Contact Email */}
          <Grid item xs={12}>
            <TextField
              required
              label="Contact email"
              fullWidth
              placeholder="Enter email address"
              name="contact_email"
              value={formikForm.values.contact_email}
              onChange={formikForm.handleChange}
              onBlur={formikForm.handleBlur}
              error={
                formikForm.touched.contact_email &&
                Boolean(formikForm.errors.contact_email)
              }
              helperText={
                formikForm.touched.contact_email &&
                formikForm.errors.contact_email
              }
            />
          </Grid>
          {/* Contact Number */}
          <Grid item xs={12}>
            <TextField
              required
              label="Contact number"
              fullWidth
              name="contact_number"
              placeholder="Enter your phone number"
              value={formikForm.values.contact_number}
              onChange={(value, country) => {
                setCountryCode(country.dialCode);
                formikForm.setFieldValue('contact_number', value);
              }}
              onBlur={handleBlur}
              error={
                formikForm.touched.contact_number &&
                Boolean(formikForm.errors.contact_number)
              }
              helperText={
                formikForm.touched.contact_number &&
                formikForm.errors.contact_number
              }
              InputProps={{
                inputComponent: PhoneInputCustom,
                inputProps: {
                  themeing: theme,
                },
                style: { height: '47px' },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleReset} title="Reset">
          Reset
        </Button>
        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PhoneInputCustom.propTypes = {
  handleActionChange: PropTypes.func.isRequired,
  themeing: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
CreateEditOrgModal.defaultProps = {
  selectedOrg: null,
};
CreateEditOrgModal.propTypes = {
  stateOfForm: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  pageIndexData: PropTypes.number.isRequired,
  pageSizeData: PropTypes.number.isRequired,
  searchTermData: PropTypes.string.isRequired,
  organizationDetailsList: PropTypes.func.isRequired,
  selectedOrg: PropTypes.number,
};

export default CreateEditOrgModal;
